import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "motion/react";
import { Menu, X, Globe, ChevronDown, Search } from "lucide-react";
import { preloadDictionary, useI18n } from "@/i18n";
import { localeNames, localeFlags, type Locale } from "@/i18n/config";
import { useSite } from "@/lib/site";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import Logo from "./Logo";
import ThemeToggle from "@/components/ui/ThemeToggle";
import { springSnappy } from "@/lib/motion";

/**
 * 頁首
 * ---------------------------------------------------------------
 * 行為：向下捲動時收起（讓內容有更多空間），向上捲動時立刻出現。
 * 這是行動裝置上很常見的模式，但很多實作會抖動 —— 關鍵是要設一個
 * 最小位移門檻（這裡是 8px），否則捲動時的微小抖動會讓它一直閃。
 */
export default function Header() {
  const { t, marketLabel } = useI18n();
  const { market, locale, path, currentPath } = useSite();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    const delta = latest - previous;
    setScrolled(latest > 8);
    if (Math.abs(delta) < 8) return;
    setHidden(delta > 0 && latest > 120);
  });

  // 換頁時關閉行動選單
  useEffect(() => setOpen(false), [currentPath]);

  /*
    導覽列用的是 nav.short.*，不是 nav.findWork 那組。
    兩者看起來重複，但用途不同：按鈕要說清楚「按下去會發生什麼」
    （Find work / 我要發案），選單只要指出「這是哪一區」（Cases / 案件）。
    共用一個 key 的下場就是選單被 CTA 的長度撐爛 —— 英文版的
    「Browse by category」原本就是首頁的區塊標題，塞進選單後換成兩行。
  */
  const links = [
    { to: "/jobs", label: t("nav.short.jobs") },
    { to: "/talent", label: t("nav.short.talent") },
    { to: "/categories", label: t("nav.short.categories") },
    { to: "/how-it-works", label: t("nav.short.howItWorks") },
    { to: "/pricing", label: t("nav.short.pricing") },
  ];

  return (
    <motion.header
      animate={{ y: hidden ? "-100%" : "0%" }}
      transition={springSnappy}
      className={cn(
        "sticky top-0 z-40 transition-all duration-300",
        // 捲動後才出現陰影 —— 頁面在最頂端時不需要視覺分隔，
        // 一開始就有陰影會讓版面顯得擁擠
        scrolled ? "bg-page/80 backdrop-blur-xl shadow-[0_4px_16px_var(--nm-shadow)]" : "bg-page/50",
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:h-18 lg:gap-6 lg:px-8">
        <Link to={path("/")} className="shrink-0" aria-label={t("brand.name")}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label={t("nav.aria.main")}>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={path(link.to)}
              className={({ isActive }) =>
                cn(
                  // whitespace-nowrap：選單項目永遠不斷行。
                  // 各語言的長度差很多，只要有一項折成兩行，整列的基線就歪掉。
                  "relative whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                  isActive ? "text-brand-800 dark:text-brand-300" : "text-ink-soft hover:text-ink",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && (
                    // layoutId 讓底線在不同連結之間「滑」過去，而不是閃現
                    <motion.span
                      layoutId="nav-active"
                      className="absolute inset-0 -z-10 rounded-pill nm-inset"
                      transition={springSnappy}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link
            to={path("/jobs")}
            className="hidden rounded-pill p-2.5 text-ink-soft transition-all hover:text-ink hover:nm-sm sm:block"
            aria-label={t("home.hero.search")}
          >
            <Search className="size-[18px]" />
          </Link>

          <ThemeToggle />
          <MarketBadge marketName={marketLabel} marketSlug={market.slug} />
          <LocaleMenu locale={locale} />

          <div className="hidden items-center gap-2 lg:flex">
            <AuthActions />
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full p-2.5 text-ink lg:hidden"
            aria-label={t("nav.menu")}
            aria-expanded={open}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-page shadow-[0_8px_20px_var(--nm-shadow)] lg:hidden"
          >
            <nav className="space-y-1 px-4 py-4" aria-label={t("nav.aria.mobile")}>
              {links.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 * index }}
                >
                  <Link
                    to={path(link.to)}
                    className="block rounded-2xl px-4 py-3 text-base font-medium text-ink transition-all hover:nm-sm"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <div className="flex gap-3 pt-3">
                <Button to={path("/login")} variant="outline" fullWidth>
                  {t("nav.login")}
                </Button>
                <Button to={path("/post-job")} fullWidth>
                  {t("nav.postJob")}
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

/**
 * 頁首右側的身分區。
 *
 * loading 態要保留位置（用一塊等寬的骨架），否則登入狀態確認後
 * 按鈕會突然變成使用者名稱，整排往左跳一下。
 */
function AuthActions() {
  const { t } = useI18n();
  const { path } = useSite();
  const { actor, loading, signOut } = useAuth();

  if (loading) return <span className="h-9 w-40 rounded-pill shimmer" aria-hidden />;

  if (!actor) {
    return (
      <>
        <Button to={path("/login")} variant="ghost" size="sm">
          {t("nav.login")}
        </Button>
        <Button to={path("/post-job")} size="sm">
          {t("nav.postJob")}
        </Button>
      </>
    );
  }

  return (
    <>
      <Link
        to={path("/dashboard")}
        className="flex items-center gap-2 rounded-pill nm-pressable px-3 py-1.5 text-sm font-medium text-ink"
      >
        <span className="flex size-6 items-center justify-center rounded-full bg-gradient-to-br from-brand-700 to-brand-950 text-[10px] font-semibold text-white">
          {actor.displayName.slice(0, 2)}
        </span>
        <span className="max-w-24 truncate">{actor.displayName}</span>
      </Link>
      <Button variant="ghost" size="sm" onClick={() => void signOut()}>
        {t("nav.logout")}
      </Button>
    </>
  );
}

/** 顯示目前市場。點下去會說明「為什麼你只看得到這個國家的案子」。 */
function MarketBadge({ marketName, marketSlug }: { marketName: string; marketSlug: string }) {
  const { t } = useI18n();
  return (
    <span
      className="hidden items-center gap-1.5 rounded-pill nm-inset-sm px-3 py-1.5 text-xs font-medium text-ink-soft sm:inline-flex"
      title={t("nav.marketHint", { market: marketName })}
    >
      <span className="text-sm leading-none">{flagFor(marketSlug)}</span>
      {marketName}
    </span>
  );
}

function LocaleMenu({ locale }: { locale: string }) {
  const { t } = useI18n();
  const { market, currentPath } = useSite();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-pill nm-pressable px-3 py-1.5 text-xs font-medium text-ink-soft hover:text-ink"
        aria-haspopup="menu"
        aria-expanded={open}
        /*
          ── 這顆按鈕在手機上沒有名字 ──────────────────────────

          底下那個語言名稱是 `hidden sm:inline` —— 手機寬度下它是
          display:none，而 display:none 的東西不在無障礙樹裡。

          於是在手機上，這顆按鈕的可及名稱是**空的**：螢幕閱讀器
          念的是「按鈕，有選單」，沒有任何線索說那是做什麼的。
          兩個圖示（地球、箭頭）都是純裝飾，補不上這個洞。

          桌機看起來沒問題，所以它躲過了每一次人工檢查 ——
          而稽核工具跑的是行動版。

          aria-label 裡要包含看得見的那段文字（語言名稱），
          否則語音控制的使用者說「點繁體中文」會找不到這顆按鈕
          （WCAG 2.5.3，Label in Name）。
        */
        aria-label={t("nav.language", { name: localeNames[locale as Locale] ?? locale })}
      >
        <Globe className="size-3.5" aria-hidden />
        <span className="hidden sm:inline">{localeNames[locale as Locale] ?? locale}</span>
        <ChevronDown className={cn("size-3 transition-transform", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
            <motion.ul
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.16 }}
              className="absolute right-0 z-20 mt-2 min-w-44 overflow-hidden rounded-2xl nm-lg p-1.5"
              role="menu"
            >
              {/* 只列出這個市場開放的語言 —— 台灣市場不需要顯示西班牙文 */}
              {market.locales.map((code) => (
                <li key={code}>
                  {/*
                    滑過就先把字典載下來。

                    字典是動態 import 的 chunk（約 130KB）。不先載的話，
                    點下去的那一刻 locale 已經換了、字典還沒到，畫面會
                    停在**舊語言**一小段時間 —— 使用者按了「日本語」，
                    看到的還是中文。

                    先載的話，點下去時 chunk 已經在 LOADED 裡，Provider
                    走同步那條路，沒有中間狀態。

                    滑過不代表會點，所以這是一次可能白花的下載。但它只
                    發生在使用者已經打開語言選單、而且把游標停在某一個
                    語言上的時候 —— 那個意圖夠明確。

                    onFocus 是給鍵盤操作的：用 Tab 走到這一項時一樣先載。
                  */}
                  <Link
                    to={`/${market.slug}/${code}${currentPath === "/" ? "" : currentPath}`}
                    onClick={() => setOpen(false)}
                    onPointerEnter={() => void preloadDictionary(code)}
                    onFocus={() => void preloadDictionary(code)}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                      code === locale
                        ? "nm-inset-sm font-medium text-brand-800 dark:text-brand-300"
                        : "text-ink-soft hover:text-ink",
                    )}
                    role="menuitem"
                  >
                    <span>{localeFlags[code as Locale] ?? "🌐"}</span>
                    {localeNames[code as Locale] ?? code}
                  </Link>
                </li>
              ))}
            </motion.ul>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function flagFor(slug: string): string {
  return String.fromCodePoint(
    ...slug
      .toUpperCase()
      .split("")
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}
